import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Loader2,
  TriangleAlert,

} from 'lucide-react';
import { trpc } from '@/lib/trpc';
import { Card, CardContent,  } from '@deepguide-ai/ui/card';
import {
  getFileType,
  RequirementsPanel,
} from '@/components/evidence-viewer';
import type {
  EvidencePackViewerDTO,
  EvidenceBacklinkDTO,
} from '@deepguide-ai/types';
import { ArtifactsPanel } from '@/components/evidence-viewer/ArtifactsPanel';
import MainPreviewArea from '@/components/evidence-viewer/MainPreviewArea';


export function EvidencePack({ evidencePackId ,layout="horizontal"}: { evidencePackId: string,layout?: 'horizontal' | 'vertical' }) {


  const [selectedRequirement, setSelectedRequirement] = useState<string | null>(null);
  const [selectedBacklinkId, setSelectedBacklinkId] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<{
    type: 'screenshot' | 'attachment';
    id: string;
  } | null>(null);
  const [highlightParagraphs, setHighlightParagraphs] = useState<number[]>([]);
  const [highlightText, setHighlightText] = useState<string | null>(null);
  const [pdfPage, setPdfPage] = useState<number | null>(null);
  const [highlightBox, setHighlightBox] = useState<{
    x: number;
    y: number;
    width: number;
    height: number;
  } | null>(null);

  // Fetch evidence pack data
  const packQuery = useQuery({
    ...trpc.evidencePack.getForViewer.queryOptions({ evidencePackId }),
    enabled: !!evidencePackId,
  });

  // Fetch backlinks
  const backlinksQuery = useQuery({
    ...trpc.evidencePack.getBacklinks.queryOptions({ evidencePackId }),
    enabled: !!evidencePackId,
  });

  const pack = packQuery.data as EvidencePackViewerDTO | undefined;
  const backlinks = (backlinksQuery.data || []) as EvidenceBacklinkDTO[];

  // Handle backlink click - navigate to the linked file and highlight
  const handleBacklinkClick = (backlink: EvidenceBacklinkDTO) => {
    if (!pack) return;

    // Track selected backlink
    setSelectedBacklinkId(backlink.id);

    // Determine file type and select it
    if (backlink.screenshotId) {
      setSelectedFile({ type: 'screenshot', id: backlink.screenshotId });
      setHighlightParagraphs([]);
      setHighlightText(null);
      setPdfPage(null);
      // Extract bounding box if available
      const location = backlink.location as { boundingBox?: { x: number; y: number; width: number; height: number } };
      setHighlightBox(location?.boundingBox ?? null);
      return;
    }

    if (!backlink.evidenceFileId) {
      setHighlightParagraphs([]);
      setHighlightText(null);
      setPdfPage(null);
      return;
    }

    setSelectedFile({ type: 'attachment', id: backlink.evidenceFileId });

    const attachment = pack.artifacts.attachments.find(
      (a) => a.id === backlink.evidenceFileId
    );
    const attachmentType = attachment
      ? getFileType(attachment.mimeType, attachment.filename)
      : null;

    const matchedText = backlink.matchedText?.trim();

    if (attachmentType === 'docx') {
      if (matchedText) {
        setHighlightText(matchedText);
        setHighlightParagraphs([]);
      } else if (backlink.location && 'paragraph' in backlink.location) {
        const paraIndex = (backlink.location as { paragraph?: number }).paragraph;
        const zeroBased = typeof paraIndex === 'number' ? paraIndex - 1 : null;
        if (zeroBased !== null && zeroBased >= 0) {
          setHighlightParagraphs([zeroBased]);
          setHighlightText(null);
        } else {
          setHighlightParagraphs([]);
          setHighlightText(null);
        }
      } else {
        setHighlightParagraphs([]);
        setHighlightText(null);
      }
    } else {
      setHighlightParagraphs([]);
      setHighlightText(null);
    }

    // Extract PDF page if available
    if (
      attachmentType === 'pdf' &&
      backlink.location &&
      'page' in backlink.location
    ) {
      const pageNumber = (backlink.location as { page?: number }).page;
      setPdfPage(pageNumber ?? null);
    } else {
      setPdfPage(null);
    }
  };

  // Handle requirement selection - auto-select first linked file if available
  const handleRequirementSelect = (reqId: string | null) => {
    setSelectedRequirement(reqId);

    if (reqId) {
      // Find backlinks for this requirement
      const reqBacklinks = backlinks.filter((bl) => bl.requirementId === reqId);
      if (reqBacklinks.length > 0) {
        // Auto-select the first linked file
        handleBacklinkClick(reqBacklinks[0]);
      } else {
        // Clear highlight if no backlinks
        setHighlightParagraphs([]);
        setHighlightText(null);
        setPdfPage(null);
      }
    } else {
      setHighlightParagraphs([]);
      setHighlightText(null);
      setPdfPage(null);
    }
  };

  const handleSelectFile = (file: { type: 'screenshot' | 'attachment'; id: string } | null) => {
    setSelectedFile(file);
    setSelectedBacklinkId(null); // Clear backlink selection when selecting file directly
    setHighlightParagraphs([]);
    setHighlightText(null);
    setPdfPage(null);
    setHighlightBox(null);
  };

  if (packQuery.isLoading) {
    return (

      <div className={`flex ${layout === 'vertical' ? 'h-10 px-4 py-3' : 'h-screen'} w-full items-center justify-center gap-2 text-sm text-muted-foreground`}>
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>

    );
  }

  if (packQuery.error || !pack) {
    return (

      <div className={`flex ${layout === 'vertical' ? 'h-10 px-4 py-3' : 'h-screen'} w-full flex-col items-center justify-center gap-2 text-center text-sm text-destructive`}>
        <TriangleAlert className="h-6 w-6" />
        <span className={layout === 'vertical' ? 'line-clamp-2 max-w-full break-words' : 'max-w-full break-words'}>
          {packQuery.error?.message || 'Evidence pack not found'}
        </span>
      </div>

    );
  }

  return (
    <div className={'w-full mt-3 flex flex-col box-content space-y-6 pb-12'}>
      <div className="grid grid-cols-12 gap-6">
        {/* Left Sidebar - Requirements */}
        <div className={` ${layout === 'vertical' ? 'col-span-12' : 'col-span-5'} space-y-4`}>
          <RequirementsPanel
            evidencePackId={evidencePackId!}
            requirements={pack.requirements}
            completeness={pack.completeness}
            backlinks={backlinks}
            permissions={pack.permissions}
            elementScores={pack.internal?.elementScores}
            selectedRequirement={selectedRequirement}
            selectedBacklinkId={selectedBacklinkId}
            onSelectRequirement={handleRequirementSelect}
            onBacklinkClick={handleBacklinkClick}
            layout={layout}
          />

          <ArtifactsPanel
            artifacts={pack.artifacts}
            selectedFile={selectedFile}
            onSelectFile={handleSelectFile}
            layout={layout}
          />
        </div>

        {/* Center - Preview Area */}
        <div className={` ${layout === 'vertical' ? 'col-span-12 h-[640px]' : 'col-span-7 h-[920px]'} flex min-h-0 flex-col '}`}>
          <Card className="flex min-h-0 flex-1 flex-col shadow-none">
            <CardContent className="flex min-h-0 flex-1 flex-col px-6 py-0">
              <MainPreviewArea
                pack={pack}
                selectedFile={selectedFile}
                highlightParagraphs={highlightParagraphs}
                highlightText={highlightText || undefined}
                pdfPage={pdfPage}
                highlightBox={highlightBox}
                selectedRequirementId={selectedRequirement}
              />
            </CardContent>
          </Card>
        </div>
      </div>
    </div>

  );
}
